// Precision (confidence-interval width) planning: the interval a study of n would report if its
// estimate came out at the planning value, and the smallest n that makes it narrow enough.
// Checked against scipy/statsmodels and published examples by spec/javascript/precision_check.mjs.
//   mean: t interval, x̄ ± t(1 − α/2, n − 1) · s / √n, with s = the planning SD
//   correlation: Fisher z interval, tanh(atanh r ± z / √(n − 3))
//   icc: the exact F interval for a single-rater consistency ICC with k raters (Shrout & Fleiss, 1979;
//     McGraw & Wong, 1996), the interval Bonett's (2002) sample-size approximation is built for:
//     F₀ = (1 + (k − 1)ρ) / (1 − ρ), F_L = F₀ / F(1 − α/2; n − 1, (n − 1)(k − 1)), F_U = F₀ · F(1 − α/2; (n − 1)(k − 1), n − 1),
//     limits (F − 1) / (F + k − 1). model "one_way" uses n(k − 1) error df.
//   alpha: Feldt's F interval (Feldt, Woodruff & Salih, 1987), 1 − (1 − α̂) F(1 − α/2 or α/2; n − 1, (n − 1)(k − 1))
//   kappa: κ ± z · √(Q / n), Q = n × Fleiss, Cohen & Everitt's (1969) large-sample variance, for a table
//     with category shares p (the same for both raters) and agreement κ: p_ij = (1 − κ) p_i p_j + κ p_i [i = j].
//     With two categories this is the table Cantor (1996) plans from.
//   proportion: Wilson score interval
import { normalQuantile, tQuantile, fQuantile } from "./power.js";

export const MIN_N = { mean: 2, correlation: 4, icc: 3, alpha: 3, kappa: 2, proportion: 1 };

export function kappaTable(kappa, shares) {
	const total = shares.reduce((s, x) => s + x, 0);
	const p = shares.map((x) => x / total);
	return p.map((pi, i) => p.map((pj, j) => (1 - kappa) * pi * pj + (i === j ? kappa * pi : 0)));
}

// n × the large-sample variance of κ̂ (Fleiss, Cohen & Everitt, 1969, Eq. 13), for table proportions p.
export function kappaQ(p) {
	const k = p.length;
	const row = p.map((r) => r.reduce((s, x) => s + x, 0));
	const col = p[0].map((_, j) => p.reduce((s, r) => s + r[j], 0));
	const po = p.reduce((s, r, i) => s + r[i], 0);
	const pe = row.reduce((s, x, i) => s + x * col[i], 0);
	let a = 0, b = 0;
	for (let i = 0; i < k; i++) {
		a += p[i][i] * ((1 - pe) - (col[i] + row[i]) * (1 - po)) ** 2;
		for (let j = 0; j < k; j++) if (i !== j) b += p[i][j] * (col[i] + row[j]) ** 2;
	}
	const c = (po * pe - 2 * pe + po) ** 2;
	return (a + (1 - po) ** 2 * b - c) / (1 - pe) ** 4;
}

// The interval [lower, upper] a study of n would report at the planning values.
export function interval({ method, n, conf = 0.95, guess = 0.5, sd = 1, k = 2, shares = [0.5, 0.5], model = "two_way" }) {
	const a = 1 - conf;
	switch (method) {
		case "mean": {
			// A t interval needs n ≥ 2 and a positive SD; anything else has no interval.
			if (!(n >= 2) || !(sd > 0)) return [NaN, NaN];
			const h = (tQuantile(1 - a / 2, n - 1) * sd) / Math.sqrt(n);
			return [guess - h, guess + h];
		}
		case "correlation": {
			const z = Math.atanh(guess), c = normalQuantile(1 - a / 2) / Math.sqrt(n - 3);
			return [Math.tanh(z - c), Math.tanh(z + c)];
		}
		case "icc": {
			const f0 = (1 + (k - 1) * guess) / (1 - guess);
			const v1 = (n - (model === "one_way" ? 0 : 1)) * (k - 1), v2 = n - 1;
			const fl = f0 / fQuantile(1 - a / 2, v2, v1), fu = f0 * fQuantile(1 - a / 2, v1, v2);
			return [(fl - 1) / (fl + k - 1), (fu - 1) / (fu + k - 1)];
		}
		case "alpha": {
			const d1 = n - 1, d2 = (n - 1) * (k - 1);
			return [1 - (1 - guess) * fQuantile(1 - a / 2, d1, d2), 1 - (1 - guess) * fQuantile(a / 2, d1, d2)];
		}
		case "kappa": {
			const h = normalQuantile(1 - a / 2) * Math.sqrt(kappaQ(kappaTable(guess, shares)) / n);
			return [guess - h, guess + h];
		}
		case "proportion": {
			const z = normalQuantile(1 - a / 2), z2 = z * z;
			const centre = (guess + z2 / (2 * n)) / (1 + z2 / n);
			const h = (z / (1 + z2 / n)) * Math.sqrt((guess * (1 - guess)) / n + z2 / (4 * n * n));
			return [centre - h, centre + h];
		}
		default:
			throw new Error(`unknown precision method ${method}`);
	}
}

export function width(args) {
	const [lo, hi] = interval(args);
	return hi - lo;
}

// Smallest n whose interval is no wider than `target` (the full width, or ± target when `half`).
// Width shrinks as n grows, so this is a binary search. null if more than maxN.
export function sampleSizeForWidth({ target, half = false, maxN = 20000, ...args }) {
	const full = half ? 2 * target : target;
	const ok = (n) => width({ ...args, n }) <= full;
	if (!(full > 0) || !ok(maxN)) return null;
	let lo = MIN_N[args.method] || 2, hi = maxN;
	while (lo < hi) {
		const mid = Math.floor((lo + hi) / 2);
		if (ok(mid)) hi = mid; else lo = mid + 1;
	}
	return lo;
}
